import * as L from "leaflet";
import { INFO_CARD_TYPE } from "@/features/cards/shared/card-routing/card-types";
import {
    calculateCardHeight,
    calculateCardWidth,
    getEffectiveChipsHeight,
    isCardFullWidthLayout,
} from "@/features/cards/shared/card-shell/card-geometry";
import { alwaysFullWidthCardTypes, CARD_SLOT } from "@/features/cards/shared/card-routing/card-config";
import { useStore } from "@/store/store";
import type MapEntityBase from "../map-entities/map-entity-base";
import { MapComponent } from "../map-helper";
import { centerBoundsInViewableArea, centerPointInViewableArea } from "./pan";

interface DoPanArgs {
    entity: MapEntityBase;
    rightCardWidth: number;
    rightCardHeight: number;
    leftCardWidth: number;
    leftCardHeight: number;
    chipsHeight: number;
    cardType: INFO_CARD_TYPE | undefined;
    avoidX: boolean;
    avoidLeftX: boolean;
    avoidY: boolean;
    shouldPanRegardlessOfObscuration?: boolean;
    zoomDiffThreshold?: number;
    padRatio?: number;
}

interface PanOptions {
    shouldPanRegardlessOfObscuration?: boolean;
    /** Override the zoom-diff threshold (default 4) */
    zoomDiffThreshold?: number;
    /** Override the bounds pad ratio (default 0.2). */
    padRatio?: number;
}

/**
 * Card-aware panning function that centers the selected entity in the viewable area of the map.
 * Reads all state imperatively at call time — no reactive subscriptions, no re-renders.
 */
export const panTo = (entity: MapEntityBase | undefined | null, options?: PanOptions): void => {
    if (!entity) return;
    const layout = readCardLayout();
    doPan({
        entity,
        ...layout,
        shouldPanRegardlessOfObscuration: options?.shouldPanRegardlessOfObscuration,
        zoomDiffThreshold: options?.zoomDiffThreshold,
        padRatio: options?.padRatio,
    });
};

/**
 * Internal function that performs the actual panning to center the selected entity in the viewable area of the map.
 */
const doPan = ({
    entity,
    rightCardWidth,
    rightCardHeight,
    leftCardWidth,
    leftCardHeight,
    chipsHeight,
    cardType,
    avoidX,
    avoidLeftX,
    avoidY,
    shouldPanRegardlessOfObscuration = true,
    zoomDiffThreshold,
    padRatio,
}: DoPanArgs): void => {
    // Polygon-based entities (AOI, Zone) have leafletElement, so we can pan to their bounds
    if (entity.leafletElement) {
        const bounds = (entity.leafletElement as { getBounds(): L.LatLngBounds }).getBounds();
        // Geometry may not be loaded yet; invalid bounds cause Leaflet to throw
        if (!bounds.isValid()) return;
        centerBoundsInViewableArea({
            mapComponent: MapComponent,
            bounds,
            rightCardWidth,
            rightCardHeight,
            leftCardWidth,
            leftCardHeight,
            chipsHeight,
            cardTypeInRightSlot: cardType,
            avoidX,
            avoidLeftX,
            avoidY,
            shouldPanRegardlessOfObscuration,
            zoomDiffThreshold,
            padRatio,
        });
    } else {
        // Other marker-like entities have LatLng instead of bounds
        centerPointInViewableArea({
            mapComponent: MapComponent,
            rightCardWidth,
            rightCardHeight,
            leftCardWidth,
            leftCardHeight,
            chipsHeight,
            cardTypeInRightSlot: cardType,
            latLng: new L.LatLng(entity.lat, entity.long),
            avoidX,
            avoidLeftX,
            avoidY,
            shouldPanRegardlessOfObscuration,
        });
    }
};

/**
 * Returns measured right card dimensions, falling back to calculated estimates when the card is
 * open but not yet painted (height or width is 0).
 */
const computeEffectiveRightCardDimensions = (
    rightCard: { isOpen: boolean; height: number; width: number; cardType: INFO_CARD_TYPE | undefined },
    chipsHeight: number,
): { height: number; width: number } => {
    if (!(rightCard.height === 0 || rightCard.width === 0) || !rightCard.isOpen) {
        return { height: rightCard.height, width: rightCard.width };
    }
    const effectiveChipsHeight = getEffectiveChipsHeight(chipsHeight, rightCard.cardType);
    const isRightFullWidthLayout = isCardFullWidthLayout(rightCard.cardType);
    // alwaysFullWidthCardTypes are full-width even on desktop.
    // Function calculateCardHeight(isMobile=true) on desktop returns ~window.innerHeight so we only pass true for truly responsive cards.
    const isAlwaysFullWidth = rightCard.cardType !== undefined && alwaysFullWidthCardTypes.has(rightCard.cardType);
    const isMobileHeightFallback = isRightFullWidthLayout && !isAlwaysFullWidth;
    return {
        height:
            rightCard.height === 0
                ? calculateCardHeight(isMobileHeightFallback, effectiveChipsHeight)
                : rightCard.height,
        width: rightCard.width === 0 ? calculateCardWidth(rightCard.cardType, isRightFullWidthLayout) : rightCard.width,
    };
};

/** Reads the current card layout from the store, mirroring panTo's state-reading logic. */
const readCardLayout = () => {
    const state = useStore.getState();
    const rightCard = state.slots[CARD_SLOT.BOTTOM_RIGHT];
    const leftCard = state.slots[CARD_SLOT.BOTTOM_LEFT];

    const avoidY =
        (rightCard.isOpen && isCardFullWidthLayout(rightCard.cardType)) ||
        (leftCard.isOpen && isCardFullWidthLayout(leftCard.cardType));
    const avoidX = rightCard.isOpen && !isCardFullWidthLayout(rightCard.cardType);
    const avoidLeftX = leftCard.isOpen && !isCardFullWidthLayout(leftCard.cardType);

    const effectiveLeftCardWidth = leftCard.isOpen && !isCardFullWidthLayout(leftCard.cardType) ? leftCard.width : 0;
    const effectiveLeftCardHeight = leftCard.isOpen ? leftCard.height : 0;

    const { height: effectiveRightHeight, width: effectiveRightWidth } = computeEffectiveRightCardDimensions(
        rightCard,
        state.chipsHeight,
    );

    return {
        rightCardWidth: effectiveRightWidth,
        rightCardHeight: effectiveRightHeight,
        leftCardWidth: effectiveLeftCardWidth,
        leftCardHeight: effectiveLeftCardHeight,
        chipsHeight: state.chipsHeight,
        cardType: rightCard.cardType,
        avoidX,
        avoidLeftX,
        avoidY,
    };
};

/**
 * Card-aware panning to raw lat/lng coordinates.
 * Use when no MapEntityBase is available (e.g. typed coordinate entry, fallback positions).
 */
export const panToCoords = (lat: number, lng: number, options?: { zoom?: number }): void => {
    const {
        rightCardWidth,
        rightCardHeight,
        leftCardWidth,
        leftCardHeight,
        chipsHeight,
        cardType,
        avoidX,
        avoidLeftX,
        avoidY,
    } = readCardLayout();
    centerPointInViewableArea({
        mapComponent: MapComponent,
        rightCardWidth,
        rightCardHeight,
        leftCardWidth,
        leftCardHeight,
        chipsHeight,
        cardTypeInRightSlot: cardType,
        latLng: new L.LatLng(lat, lng),
        avoidX,
        avoidLeftX,
        avoidY,
        newZoom: options?.zoom,
        // Callers (search, AOI lat/lng, initial fallback) always intend to pan;
        // without this flag the map silently no-ops when no card is obscuring the target.
        shouldPanRegardlessOfObscuration: true,
    });
};

/**
 * Card-aware panning to raw LatLngBounds.
 * Use when bounds are computed independently (e.g. combined bounds of multiple AOIs).
 */
export const panToBounds = (bounds: L.LatLngBounds, options?: PanOptions): void => {
    const {
        rightCardWidth,
        rightCardHeight,
        leftCardWidth,
        leftCardHeight,
        chipsHeight,
        cardType,
        avoidX,
        avoidLeftX,
        avoidY,
    } = readCardLayout();
    centerBoundsInViewableArea({
        mapComponent: MapComponent,
        bounds,
        rightCardWidth,
        rightCardHeight,
        leftCardWidth,
        leftCardHeight,
        chipsHeight,
        cardTypeInRightSlot: cardType,
        avoidX,
        avoidLeftX,
        avoidY,
        shouldPanRegardlessOfObscuration: options?.shouldPanRegardlessOfObscuration,
        zoomDiffThreshold: options?.zoomDiffThreshold,
        padRatio: options?.padRatio,
    });
};
